import { z } from 'zod';
import { FollowUpEntityTypeSchema, FollowUpStatusSchema } from '../lib/enums';
import { MarkupSchema, zDateOnly, zEmail, zIsoDateTime, zStateCode, zUuid } from './common';
import { EstimateSummarySchema } from './estimates';
// -------------------------------------------------------------------- clients
/**
 * Per-client settings — the FIRST client-level settings tier in the codebase.
 * It mirrors `CompanySettingsSchema` deliberately: same shape, same "null or
 * absent means inherit" convention, so the resolution chain stays readable.
 *
 * These are DEFAULTS FOR NEW ESTIMATES, not live overrides. They are resolved
 * once, at estimate creation, and copied onto the estimate row (the existing
 * copy-at-create doctrine), so editing a client can never silently reprice an
 * estimate that has already been sent.
 */
export const ClientSettingsSchema = z
    .object({
    /** Applies to `kind: 'material'` lines only. Beats the company default. */
    defaultMaterialsMarkup: MarkupSchema.nullable().optional(),
})
    .strict();
export const ClientSchema = z.object({
    id: zUuid,
    name: z.string().min(1).max(300),
    email: zEmail.nullable(),
    phone: z.string().max(40).nullable(),
    notes: z.string().max(10000).nullable(),
    settings: ClientSettingsSchema,
    createdAt: zIsoDateTime,
    updatedAt: zIsoDateTime,
});
export const CreateClientRequestSchema = z
    .object({
    name: z.string().min(1).max(300),
    email: zEmail.nullable().optional(),
    phone: z.string().max(40).nullable().optional(),
    notes: z.string().max(10000).nullable().optional(),
    settings: ClientSettingsSchema.optional(),
})
    .strict();
export const UpdateClientRequestSchema = CreateClientRequestSchema.partial();
// ------------------------------------------------------------------- contacts
export const ClientContactSchema = z.object({
    id: zUuid,
    clientId: zUuid,
    name: z.string().min(1).max(200),
    email: zEmail.nullable(),
    phone: z.string().max(40).nullable(),
    title: z.string().max(120).nullable(),
    isPrimary: z.boolean(),
});
export const CreateClientContactRequestSchema = z
    .object({
    name: z.string().min(1).max(200),
    email: zEmail.nullable().optional(),
    phone: z.string().max(40).nullable().optional(),
    title: z.string().max(120).nullable().optional(),
    isPrimary: z.boolean().optional(),
})
    .strict();
export const UpdateClientContactRequestSchema = CreateClientContactRequestSchema.partial();
// ---------------------------------------------------------------------- sites
/**
 * Project site — a CLIENT's address record. `jurisdictionState` drives
 * compliance disclosure selection.
 *
 * NAMING: this is NOT related to `Project` in ./projects. It is a child of a
 * client, and a `Project` points UP at one via `siteId`. Nothing in the
 * projects feature is named `*Site*` as a noun.
 */
export const ProjectSiteSchema = z.object({
    id: zUuid,
    clientId: zUuid,
    label: z.string().max(200).nullable(),
    address1: z.string().min(1).max(300),
    address2: z.string().max(300).nullable(),
    city: z.string().min(1).max(120),
    jurisdictionState: zStateCode,
    postalCode: z.string().max(20).nullable(),
});
export const CreateProjectSiteRequestSchema = z
    .object({
    label: z.string().max(200).nullable().optional(),
    address1: z.string().min(1).max(300),
    address2: z.string().max(300).nullable().optional(),
    city: z.string().min(1).max(120),
    jurisdictionState: zStateCode,
    postalCode: z.string().max(20).nullable().optional(),
})
    .strict();
export const UpdateProjectSiteRequestSchema = CreateProjectSiteRequestSchema.partial();
// ----------------------------------------------------------- activity + tasks
/** Append-only client timeline entry (system- and user-generated). */
export const ClientActivitySchema = z.object({
    id: zUuid,
    clientId: zUuid,
    kind: z.string().min(1).max(64),
    message: z.string().max(2000),
    entityType: z.string().max(64).nullable(),
    entityId: zUuid.nullable(),
    actorUserId: zUuid.nullable(),
    createdAt: zIsoDateTime,
});
export const CreateClientActivityRequestSchema = z
    .object({
    kind: z.string().min(1).max(64),
    message: z.string().min(1).max(2000),
})
    .strict();
export const FollowUpReminderSchema = z.object({
    id: zUuid,
    entityType: FollowUpEntityTypeSchema,
    entityId: zUuid,
    dueAt: zDateOnly,
    note: z.string().max(1000),
    status: FollowUpStatusSchema,
    createdAt: zIsoDateTime,
});
export const CreateFollowUpReminderRequestSchema = z
    .object({
    entityType: FollowUpEntityTypeSchema,
    entityId: zUuid,
    dueAt: zDateOnly,
    note: z.string().min(1).max(1000),
})
    .strict();
export const UpdateFollowUpReminderRequestSchema = z
    .object({
    dueAt: zDateOnly.optional(),
    note: z.string().min(1).max(1000).optional(),
    status: FollowUpStatusSchema.optional(),
})
    .strict();
// ------------------------------------------------------------------ composite
export const ClientDetailResponseSchema = z.object({
    client: ClientSchema,
    contacts: z.array(ClientContactSchema),
    sites: z.array(ProjectSiteSchema),
    reminders: z.array(FollowUpReminderSchema),
});
export const ClientEstimatesResponseSchema = z.array(EstimateSummarySchema);
//# sourceMappingURL=clients.js.map